import { motion, useInView, animate } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../../config/api';

function AnimatedNumber({ value, suffix = '', static: isStatic = false }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [display, setDisplay] = useState(isStatic ? value : 0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isStatic || hasAnimated.current) return;
    if (isInView) {
      hasAnimated.current = true;
      const controls = animate(0, value, {
        duration: 2,
        ease: [0.25, 0.1, 0.25, 1],
        onUpdate: (v) => setDisplay(Math.round(v * 10) / 10),
        onComplete: () => setDisplay(value),
      });
      return () => controls.stop();
    }
  }, [isInView, value, isStatic]);

  return (
    <span ref={ref} className="gradient-text text-4xl md:text-5xl font-black font-headline">
      {display}{suffix}
    </span>
  );
}

export default function StatsCounter() {
  const { t } = useTranslation();
  const [liveStats, setLiveStats] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/metrics/public/stats`)
      .then((res) => {
        if (!res.ok) return null;
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) return null;
        return res.json();
      })
      .then((data) => {
        if (!data || typeof data !== 'object' || Array.isArray(data)) return;
        const n = (v) => {
          const x = Number(v);
          return Number.isFinite(x) && x >= 0 && x < 1e12 ? x : undefined;
        };
        setLiveStats({
          companies: n(data.companies),
          countries: n(data.countries),
          transactions: n(data.transactions),
        });
      })
      .catch(() => {});
  }, []);

  const stats = [
    { value: liveStats?.companies ?? 216, suffix: '+', labelKey: 'stats.companies' },
    { value: 7, suffix: '+', labelKey: 'stats.experience' },
    { value: liveStats?.countries ?? 70, suffix: '+', labelKey: 'stats.countries' },
    { value: liveStats?.transactions ?? 24, suffix: '+', labelKey: 'stats.transactions' },
  ];

  return (
    <section className="relative py-16 bg-transparent overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] rounded-full blur-[120px] opacity-[0.06]" style={{ background: 'radial-gradient(ellipse, #D23CB3, #A84397, transparent)' }} />
      <div className="relative z-10 mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.labelKey}
              className="text-center"
              initial={{ opacity: 0.5, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ delay: i * 0.1, duration: 0.4 }}
            >
              <AnimatedNumber value={s.value} suffix={s.suffix} static={s.static} />
              <p className="text-xs uppercase tracking-[3px] text-on-surface-variant/60 font-label mt-2">{t(s.labelKey)}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
